import { useState } from 'react';
import { useSaveTwilioCampaignProfile } from 'components/admin/businesses/twilioIntegration/mutations/useSaveTwilioCampaignProfile';
import { useInvalidateTwilioIntegration } from 'components/admin/businesses/twilioIntegration/queries/useGetTwilioIntegration';
import {
  buildDefaultTwilioCampaignDescription,
  TWILIO_CAMPAIGN_DESCRIPTION_MAX_LENGTH,
  TWILIO_CAMPAIGN_DESCRIPTION_MIN_LENGTH,
} from 'constants/twilio/twilioCampaign';
import { CampaignProfile } from 'types/twilio/campaignProfile';
import { CreateTwilioCustomerProfileOptions } from 'types/twilio/twilioCustomerProfile';

interface UseCampaignProfileArgs {
  businessId: number;
  savedCampaignProfile: CampaignProfile | null | undefined;
  customerProfileOptions: CreateTwilioCustomerProfileOptions;
}

export const useCampaignProfile = ({
  businessId,
  savedCampaignProfile,
  customerProfileOptions,
}: UseCampaignProfileArgs) => {
  const [campaignDescription, setCampaignDescription] = useState<string>(
    savedCampaignProfile?.description ??
      buildDefaultTwilioCampaignDescription(
        customerProfileOptions.legalBusinessName,
        customerProfileOptions.businessWebSite,
      ),
  );

  const { mutateAsync: saveCampaignProfileAsync, isLoading: isSavingCampaignProfile } =
    useSaveTwilioCampaignProfile({
      successCallback: useInvalidateTwilioIntegration(businessId),
    });

  const trimmedCampaignDescriptionLength = campaignDescription.trim().length;
  const isCampaignDescriptionValid =
    trimmedCampaignDescriptionLength >= TWILIO_CAMPAIGN_DESCRIPTION_MIN_LENGTH &&
    trimmedCampaignDescriptionLength <= TWILIO_CAMPAIGN_DESCRIPTION_MAX_LENGTH;

  const buildCampaignProfilePayload = (): CampaignProfile => ({
    legalBusinessName: customerProfileOptions.legalBusinessName,
    businessWebSite: customerProfileOptions.businessWebSite,
    businessType: customerProfileOptions.businessType,
    businessRegistrationIdentifier: customerProfileOptions.businessRegistrationIdentifier,
    businessRegistrationNumber: customerProfileOptions.businessRegistrationNumber,
    description: campaignDescription.trim(),
  });

  const handleSaveCampaignProfile = () =>
    saveCampaignProfileAsync({ businessId, campaignProfile: buildCampaignProfilePayload() });

  const resetCampaignDescription = () =>
    setCampaignDescription(
      buildDefaultTwilioCampaignDescription(
        customerProfileOptions.legalBusinessName,
        customerProfileOptions.businessWebSite,
      ),
    );

  const onCampaignDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setCampaignDescription(event.target.value);
  };

  return {
    campaignDescription,
    onCampaignDescriptionChange,
    trimmedCampaignDescriptionLength,
    isCampaignDescriptionValid,
    isSavingCampaignProfile,
    handleSaveCampaignProfile,
    resetCampaignDescription,
  };
};
